import React, { useCallback, useEffect, useRef } from "react";
import { Switch, Route } from "react-router-dom";
import AppLayout from "./containers/AppLayout";
import { useSelector } from "react-redux";
import { getIsSignedIn } from "./store/user/selectors";
import SignInLoading from "./pages/SignInLoading";
import {
  ReactPlugin,
  withAITracking,
} from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory } from "history";
import config, { updateConfig } from "./config";
import { initTracking, ITrackingConfig } from "@creditsafe/tracking";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import { subscribeToLogout } from "./utils/broadcastLogout";
import {
  ACTIVITY_STORAGE_KEY,
  startInactivityLogoutWatcher,
} from "./utils/inactivityLogout";
import { logout, syncLogoutFromOtherTab } from "./utils/logout";
const reactPlugin = new ReactPlugin();
const cloudFeatureName = "UseAzure_DataCleaning";
initTracking({
  instrumentationKey: config.INSTRUMENTATION_KEY,
  extensions: [reactPlugin],
  extensionConfig: {
    [reactPlugin.identifier]: {
      history: createBrowserHistory({ basename: "" }),
    },
  },
} as ITrackingConfig);

const App: React.FC = () => {
  const isSignedIn = useSelector(getIsSignedIn);
  const skipActivity = useRef<boolean>(false);
  const debouncedActivityTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const updateActivity = useCallback((): void => {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()));
  }, []);

  const initializeActivity = useCallback((): void => {
    const existingActivity = localStorage.getItem(ACTIVITY_STORAGE_KEY);

    // Keep the existing idle clock across auth-driven remounts.
    if (!existingActivity) {
      updateActivity();
    }
  }, [updateActivity]);

  const isBrowserReload = useCallback((): boolean => {
    const nav = performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming;
    return nav?.type === "reload";
  }, []);

  const handleLogout = useCallback((): void => {
    logout();
  }, []);

  useEffect(() => {
    const stopInactivityWatcher = startInactivityLogoutWatcher({
      onUserIdle: handleLogout,
    });

    return () => {
      stopInactivityWatcher();
    };
  }, [handleLogout]);

  useEffect(() => {
    return subscribeToLogout(() => {
      skipActivity.current = true;
      syncLogoutFromOtherTab();
    });
  }, []);

  useEffect(() => {
    if (isBrowserReload()) {
      updateActivity();
    } else {
      initializeActivity();
    }

    const handleActivity = (): void => {
      if (!skipActivity.current) {
        updateActivity();
      }
    };

    const handleDebouncedActivity = (): void => {
      if (debouncedActivityTimeout.current) {
        clearTimeout(debouncedActivityTimeout.current);
      }

      debouncedActivityTimeout.current = setTimeout(() => {
        if (!skipActivity.current) {
          updateActivity();
        }
      }, 150);
    };

    window.addEventListener("click", handleActivity);
    window.addEventListener("scroll", handleDebouncedActivity, true);
    // Update activity on browser back/forward navigation
    window.addEventListener("popstate", handleActivity);

    return () => {
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("scroll", handleDebouncedActivity, true);
      window.removeEventListener("popstate", handleActivity);

      if (debouncedActivityTimeout.current) {
        clearTimeout(debouncedActivityTimeout.current);
      }
    };
  }, [initializeActivity, isBrowserReload, updateActivity]);

  if (!isSignedIn) {
    return <SignInLoading />;
  }
  return (
    <SplitTreatments names={[cloudFeatureName]}>
      {({ treatments, isReady }) => {
        if (isReady && treatments[cloudFeatureName].treatment === "on") {
          updateConfig(true);
        }
        return (
          <Switch>
            <Route component={AppLayout} />
          </Switch>
        );
      }}
    </SplitTreatments>
  );
};

export default withAITracking(reactPlugin, App);
